/**
 * Camera framing helpers for violation selection in the Forge viewer.
 */

import type { EvaluatedElement } from "@vera/rules";
import type { Violation } from "@vera/shared";
import { resolveViewerDbId } from "../lib/resolveViewerDbId";
import { focusBuildingWithElementHighlight } from "./geometry/geometryFocus";
import { elementWorldBox } from "./geometry/rayCast";
import { is2dViewer } from "./geometry/viewMode";

/** True when 3D framing should show the whole model, not tight element fit. */
export function shouldUseBuildingContextFit(
  viewer: Autodesk.Viewing.GuiViewer3D,
  dbId: number,
  elements: EvaluatedElement[],
  violation?: Violation
): boolean {
  if (is2dViewer(viewer)) return false;

  const el = elements.find(e => e.dbId === dbId);
  if (el?.ontologyType === "vera:Room") return false;

  if (violation?.provenance?.status !== "indeterminate") return false;

  const elemBox = elementWorldBox(viewer, dbId);
  const modelBox = viewer.model?.getBoundingBox?.();
  if (!elemBox || !modelBox) return true;

  const elemSize = elemBox.getSize(new THREE.Vector3());
  const modelSize = modelBox.getSize(new THREE.Vector3());
  const elemSpan = Math.max(elemSize.x, elemSize.y, elemSize.z);
  const modelSpan = Math.max(modelSize.x, modelSize.y, modelSize.z);
  if (modelSpan <= 0) return true;

  return elemSpan / modelSpan < 0.35;
}

export function fitViolationSelection(
  viewer: Autodesk.Viewing.GuiViewer3D,
  dbId: number,
  elements: EvaluatedElement[],
  violation: Violation | undefined,
  onUniversalSheet: boolean
): void {
  const element = elements.find(e => e.dbId === dbId);
  const viewDbId =
    resolveViewerDbId(viewer, dbId, element?.displayName) ?? dbId;

  if (is2dViewer(viewer)) {
    const box = elementWorldBox(viewer, viewDbId);
    const modelBox = viewer.model?.getBoundingBox?.();
    viewer.select([viewDbId]);
    if (box && modelBox) {
      const elem = box.getSize(new THREE.Vector3());
      const model = modelBox.getSize(new THREE.Vector3());
      const ratio =
        (elem.x * elem.y) / Math.max(model.x * model.y, Number.EPSILON);
      if (ratio < 0.002) {
        const span = Math.max(model.x, model.y) * 0.22;
        const c = box.getCenter(new THREE.Vector3());
        viewer.navigation.fitBounds(
          true,
          new THREE.Box3(
            new THREE.Vector3(c.x - span / 2, c.y - span / 2, modelBox.min.z),
            new THREE.Vector3(c.x + span / 2, c.y + span / 2, modelBox.max.z)
          )
        );
        return;
      }
    }
    viewer.fitToView([viewDbId], undefined, true);
    return;
  }

  if (
    onUniversalSheet &&
    shouldUseBuildingContextFit(viewer, viewDbId, elements, violation)
  ) {
    focusBuildingWithElementHighlight(viewer, viewDbId);
    return;
  }

  fitToDbIdWithContext(viewer, viewDbId);
}

const FIT_HALF_WINDOW_FRACTION_OF_MODEL_DIAGONAL = 0.12;

function fitToDbIdWithContext(
  viewer: Autodesk.Viewing.GuiViewer3D,
  dbId: number
): void {
  const model = viewer.model;
  if (!model) {
    viewer.fitToView([dbId]);
    return;
  }
  const modelBox = model.getBoundingBox?.();
  if (!modelBox) {
    viewer.fitToView([dbId]);
    return;
  }

  viewer.fitToView([dbId], undefined, true);

  const target = viewer.navigation.getTarget();
  const modelDiagonal = modelBox.min.distanceTo(modelBox.max);
  const halfWindow = modelDiagonal * FIT_HALF_WINDOW_FRACTION_OF_MODEL_DIAGONAL;

  const windowBox = modelBox.clone();
  windowBox.min.set(
    target.x - halfWindow,
    target.y - halfWindow,
    target.z - halfWindow
  );
  windowBox.max.set(
    target.x + halfWindow,
    target.y + halfWindow,
    target.z + halfWindow
  );

  viewer.navigation.fitBounds(true, windowBox);
}
